import { useState, useEffect, useCallback, useMemo } from 'react'
import { GridRowSelectionModel } from '@mui/x-data-grid'
import InfoDialog from '@/components/InfoDiaglog'

/**
 * Props for useRowSelection hook
 */
export interface UseRowSelectionProps<T = any> {
  /**
   * Data array for the current page
   */
  data?: T[]
  /**
   * Total number of items across all pages
   */
  total?: number
  /**
   * Callback when selection changes
   */
  onChangeSelected?: (selectedRows: T[], isAll: boolean) => void
  /**
   * Whether to enable selection
   */
  enabled?: boolean
}

/**
 * Return type for useRowSelection hook
 */
export interface UseRowSelectionReturn<T = any> {
  /**
   * Whether all items are selected
   */
  selectedAll: boolean
  /**
   * Current page selection IDs
   */
  currentPageSelection: (string | number)[]
  /**
   * Selected rows objects
   */
  selectedRows: T[]
  /**
   * Handler for selection change
   */
  handleSelectionChange: (newSelection: GridRowSelectionModel) => void
  /**
   * Handler for select all checkbox
   */
  handleSelectAll: (event: React.ChangeEvent<HTMLInputElement>) => void
  /**
   * Clear all selections
   */
  clearSelection: () => void
  /**
   * Whether any item is selected
   */
  isAnySelected: boolean
  /**
   * Whether the select all checkbox should show indeterminate state
   */
  isIndeterminate: boolean
}

/**
 * Hook for managing row selection in DataGrid
 */
export const useRowSelection = <T extends { id: number | string }>({
  data = [],
  total = 0,
  onChangeSelected,
  enabled = true
}: UseRowSelectionProps<T>): UseRowSelectionReturn<T> => {
  // State for selection
  const [selectedAll, setSelectedAll] = useState(false)
  const [currentPageSelection, setCurrentPageSelection] = useState<
    (string | number)[]
  >([])

  // Compute current page IDs
  const currentPageIds = useMemo(() => data.map(row => row.id), [data])

  // Compute current page length
  const currentPageLength = useMemo(() => data.length || 0, [data])

  // Get selected rows from current page data
  const selectedRows = useMemo(() => {
    if (!data.length) return []
    // Use Set for better performance with includes operation
    const selectedSet = new Set(currentPageSelection)
    return data.filter(row => selectedSet.has(row.id))
  }, [data, currentPageSelection])

  // Filter rows based on selection
  const getFilteredRows = useCallback(
    (selection: (string | number)[]) => {
      if (!data.length) return []
      const selectionSet = new Set(selection)
      return data.filter(row => selectionSet.has(row.id))
    },
    [data]
  )

  // Handle selection change
  const handleSelectionChange = useCallback(
    (newSelection: GridRowSelectionModel) => {
      if (!enabled) return

      // Batch state updates for better performance
      const batchUpdate = () => {
        setCurrentPageSelection(newSelection as (string | number)[])

        // If all items on the page are selected and this page contains all data
        // then mark as "select all"
        if (
          total === currentPageLength &&
          newSelection.length === currentPageLength &&
          currentPageLength > 0
        ) {
          setSelectedAll(true)
        }
        // If currently marked as "select all" but not all items on the page are selected
        // then unmark "select all"
        else if (selectedAll && newSelection.length < currentPageLength) {
          setSelectedAll(false)
        }
      }

      batchUpdate()

      // Call the callback with the filtered rows
      if (onChangeSelected) {
        onChangeSelected(
          getFilteredRows(newSelection as (string | number)[]) as T[],
          false
        )
      }
    },
    [
      selectedAll,
      currentPageLength,
      total,
      onChangeSelected,
      getFilteredRows,
      enabled
    ]
  )

  // Handle "select all" checkbox change
  const handleSelectAll = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!enabled) return

      const checked = event.target.checked

      // Show info dialog when selecting all
      if (checked) {
        InfoDialog.open({
          type: 'info',
          title: 'All Items Selected',
          message: 'You have selected all items across multiple pages.'
        })
      }

      // Batch state updates
      const batchUpdate = () => {
        setSelectedAll(checked)
        setCurrentPageSelection(checked ? currentPageIds : [])
      }

      batchUpdate()

      // Call the callback with all data or empty array
      if (onChangeSelected) {
        onChangeSelected(checked ? data : [], true)
      }
    },
    [currentPageIds, data, onChangeSelected, enabled]
  )

  // Reset selection when data is empty
  useEffect(() => {
    if (total === 0 && currentPageSelection.length > 0) {
      setCurrentPageSelection([])
      onChangeSelected && onChangeSelected([], false)
    }
  }, [total, onChangeSelected, currentPageSelection.length])

  // Reset selection when data changes
  useEffect(() => {
    if (!selectedAll) {
      setCurrentPageSelection([])
      onChangeSelected && onChangeSelected([], false)
    }
  }, [data, selectedAll, onChangeSelected])

  // Update selection when selectedAll changes or data changes
  useEffect(() => {
    if (selectedAll) {
      const allCurrentPageIds = data.map(row => row.id)
      setCurrentPageSelection(allCurrentPageIds)
      if (onChangeSelected) {
        onChangeSelected(data, true)
      }
    }
  }, [data, selectedAll, onChangeSelected])

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedAll(false)
    setCurrentPageSelection([])
    onChangeSelected && onChangeSelected([], false)
  }, [onChangeSelected])

  return {
    selectedAll,
    currentPageSelection,
    selectedRows,
    handleSelectionChange,
    handleSelectAll,
    clearSelection,
    isAnySelected: currentPageSelection.length > 0,
    isIndeterminate: !selectedAll && currentPageSelection.length > 0
  }
}

export default useRowSelection
